// src/pages/Performance.js
// Practice Performance Dashboard — revenue trends, denial analysis, payer performance

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts';
import * as awsApi from '../services/awsApiService';
import * as apiService from '../services/apiService';
import {
  TrendingUp, TrendingDown, DollarSign, AlertTriangle,
  CheckCircle, XCircle, BarChart3, RefreshCw, Clock, FileText, Award
} from 'lucide-react';

// Pre-computed demo stats — 6 months showing denial rate drop after FlowRCM
const buildDemoData = () => {
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ label: d.toLocaleString('default', { month: 'short', year: '2-digit' }) });
  }
  // Revenue grows, denial rate falls as FlowRCM takes effect
  const stats = [
    { billed: 42500, paid: 33150, denied: 6, total: 38 },
    { billed: 46800, paid: 37900, denied: 5, total: 40 },
    { billed: 49200, paid: 41000, denied: 4, total: 42 },
    { billed: 52400, paid: 45100, denied: 3, total: 44 },
    { billed: 55100, paid: 48700, denied: 2, total: 46 },
    { billed: 21400, paid: 19900, denied: 1, total: 19 },
  ];
  const monthlyData = months.map((m, i) => ({ ...m, ...stats[i] }));
  const denialReasons = [
    ['Missing pre-authorization', 7],
    ['Procedure not covered as submitted', 5],
    ['Duplicate claim submission', 4],
    ['Patient not eligible on date of service', 3],
    ['Frequency limitation exceeded', 3],
    ['Missing clinical documentation', 2],
  ];
  const payerData = [
    { name: 'Delta Dental PPO',    billed: 89400, paid: 77200, denied: 4,  total: 68, denialRate: 6,  collectionRate: 86 },
    { name: 'MetLife Dental',      billed: 67300, paid: 57100, denied: 5,  total: 51, denialRate: 10, collectionRate: 85 },
    { name: 'Cigna Dental',        billed: 54200, paid: 45800, denied: 3,  total: 42, denialRate: 7,  collectionRate: 85 },
    { name: 'Aetna Dental',        billed: 42100, paid: 37400, denied: 4,  total: 36, denialRate: 11, collectionRate: 89 },
    { name: 'Guardian Dental',     billed: 28500, paid: 25700, denied: 2,  total: 24, denialRate: 8,  collectionRate: 90 },
    { name: 'Blue Cross Blue Shld',billed: 21400, paid: 18600, denied: 3,  total: 18, denialRate: 17, collectionRate: 87 },
  ];
  const statusCounts = [
    { status: 'Paid',      count: 161 },
    { status: 'Submitted', count: 42 },
    { status: 'Pending',   count: 18 },
    { status: 'Denied',    count: 21 },
    { status: 'Appealed',  count: 7 },
  ];
  const totalBilled  = stats.reduce((s, m) => s + m.billed, 0);
  const totalPaid    = stats.reduce((s, m) => s + m.paid, 0);
  const totalDenied  = stats.reduce((s, m) => s + m.denied, 0);
  const totalClaims  = stats.reduce((s, m) => s + m.total, 0);
  return { monthlyData, denialReasons, payerData, statusCounts, totalBilled, totalPaid, totalDenied, totalClaims, totalPending: 60, avgDaysToPayment: 18 };
};

const Performance = () => {
  const navigate = useNavigate();
  const { practice } = useAuth();
  const appMode = apiService.getAppMode();
  const [loading, setLoading] = useState(appMode !== 'demo');
  const [claims, setClaims] = useState([]);
  const [error, setError] = useState(null);
  const [demoStats] = useState(() => appMode === 'demo' ? buildDemoData() : null);

  useEffect(() => { if (appMode !== 'demo') loadData(); }, [practice]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await awsApi.getClaims(practice?.practice_id, { limit: 500 });
      if (result.success) setClaims(result.data || []);
      else throw new Error(result.error);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (n) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

  const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);

  // ── Monthly buckets (last 6 months) ──────────────────────────────────────
  const monthlyData = appMode === 'demo' ? demoStats.monthlyData : (() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - i);
      months.push({
        label: d.toLocaleString('default', { month: 'short', year: '2-digit' }),
        year: d.getFullYear(),
        month: d.getMonth(),
        billed: 0, paid: 0, denied: 0, total: 0
      });
    }
    claims.forEach(c => {
      const d = new Date(c.service_date);
      if (isNaN(d)) return;
      const bucket = months.find(m => m.year === d.getFullYear() && m.month === d.getMonth());
      if (!bucket) return;
      bucket.billed += parseFloat(c.claim_amount || 0);
      bucket.paid   += parseFloat(c.claim_paid_amount || 0);
      if (c.claim_status === 'Denied') bucket.denied++;
      bucket.total++;
    });
    return months;
  })();

  // ── Denial reasons ────────────────────────────────────────────────────────
  const denialReasons = appMode === 'demo' ? demoStats.denialReasons : (() => {
    const map = {};
    claims.filter(c => c.claim_status === 'Denied' && c.denial_reason).forEach(c => {
      const key = c.denial_reason.length > 50 ? c.denial_reason.slice(0, 50) + '…' : c.denial_reason;
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8);
  })();

  const maxDenialCount = Math.max(...denialReasons.map(d => d[1]), 1);

  // ── Payer performance ─────────────────────────────────────────────────────
  const payerData = appMode === 'demo' ? demoStats.payerData : (() => {
    const map = {};
    claims.forEach(c => {
      const name = c.payer_name || c.payer || 'Unknown';
      if (!map[name]) map[name] = { name, billed: 0, paid: 0, denied: 0, total: 0 };
      map[name].billed += parseFloat(c.claim_amount || 0);
      map[name].paid   += parseFloat(c.claim_paid_amount || 0);
      if (c.claim_status === 'Denied') map[name].denied++;
      map[name].total++;
    });
    return Object.values(map)
      .filter(p => p.total >= 2)
      .sort((a, b) => b.billed - a.billed)
      .slice(0, 8)
      .map(p => ({ ...p, denialRate: pct(p.denied, p.total), collectionRate: pct(p.paid, p.billed) }));
  })();

  // ── Summary KPIs ──────────────────────────────────────────────────────────
  const totalBilled   = appMode === 'demo' ? demoStats.totalBilled  : claims.reduce((s, c) => s + parseFloat(c.claim_amount || 0), 0);
  const totalPaid     = appMode === 'demo' ? demoStats.totalPaid    : claims.reduce((s, c) => s + parseFloat(c.claim_paid_amount || 0), 0);
  const totalDenied   = appMode === 'demo' ? demoStats.totalDenied  : claims.filter(c => c.claim_status === 'Denied').length;
  const totalPending  = appMode === 'demo' ? demoStats.totalPending : claims.filter(c => ['Pending','Submitted','In Review'].includes(c.claim_status)).length;
  const collectionRate = pct(totalPaid, totalBilled);
  const denialRate     = appMode === 'demo' ? pct(demoStats.totalDenied, demoStats.totalClaims) : pct(totalDenied, claims.length);
  const avgDaysToPayment = appMode === 'demo' ? demoStats.avgDaysToPayment : (() => {
    const paidClaims = claims.filter(c => c.date_paid && c.service_date);
    return paidClaims.length > 0
      ? Math.round(paidClaims.reduce((s, c) => {
          const days = (new Date(c.date_paid) - new Date(c.service_date)) / (1000 * 60 * 60 * 24);
          return s + (days > 0 ? days : 0);
        }, 0) / paidClaims.length)
      : 0;
  })();

  const maxBilled = Math.max(...monthlyData.map(m => m.billed), 1);

  const statusCounts = appMode === 'demo' ? demoStats.statusCounts
    : ['Paid','Pending','Submitted','In Review','Denied','Appealed']
        .map(s => ({ status: s, count: claims.filter(c => c.claim_status === s).length }))
        .filter(s => s.count > 0);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Loading performance data...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Practice Performance</h1>
          <p className="text-sm text-slate-500">Revenue trends, denial analysis & payer performance</p>
        </div>
        <button onClick={loadData} className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">

        {error && (
          <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> {error}
          </div>
        )}

        {/* KPI Cards */}
        <div data-tour="performance-kpis" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: 'Total Billed',      value: formatCurrency(totalBilled),    icon: DollarSign,  color: 'bg-slate-100 text-slate-600' },
            { label: 'Total Collected',   value: formatCurrency(totalPaid),      icon: CheckCircle, color: 'bg-green-100 text-green-600' },
            { label: 'Collection Rate',   value: `${collectionRate}%`,           icon: TrendingUp,  color: 'bg-teal-100 text-teal-600' },
            { label: 'Denial Rate',       value: `${denialRate}%`,               icon: XCircle,     color: 'bg-red-100 text-red-600' },
            { label: 'Pending Claims',    value: totalPending,                   icon: Clock,       color: 'bg-yellow-100 text-yellow-600' },
            { label: 'Avg Days to Pay',   value: avgDaysToPayment > 0 ? `${avgDaysToPayment}d` : 'N/A', icon: BarChart3, color: 'bg-purple-100 text-purple-600' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-xs text-slate-500 mb-1">{label}</p>
              <p className="text-xl font-bold text-slate-900">{value}</p>
            </div>
          ))}
        </div>

        {/* Monthly Revenue Chart */}
        <div data-tour="performance-chart" className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-teal-600" /> Monthly Revenue — Last 6 Months
          </h2>
          {monthlyData.every(m => m.billed === 0) ? (
            <p className="text-slate-400 text-sm text-center py-8">No billing data available yet.</p>
          ) : (
            <div className="space-y-3">
              {monthlyData.map((m) => (
                <div key={m.label} className="flex items-center gap-4">
                  <div className="w-14 text-xs font-medium text-slate-500 text-right">{m.label}</div>
                  <div className="flex-1 flex items-center gap-2">
                    <div className="flex-1 bg-slate-100 rounded-full h-6 relative overflow-hidden">
                      <div
                        className="h-full bg-teal-500 rounded-full transition-all duration-500"
                        style={{ width: `${pct(m.billed, maxBilled)}%` }}
                      />
                      <div
                        className="h-full bg-green-400 rounded-full absolute top-0 transition-all duration-500"
                        style={{ width: `${pct(m.paid, maxBilled)}%` }}
                      />
                    </div>
                    <div className="w-28 text-right">
                      <span className="text-sm font-semibold text-slate-900">{formatCurrency(m.billed)}</span>
                      {m.paid > 0 && <span className="text-xs text-green-600 block">{formatCurrency(m.paid)} paid</span>}
                    </div>
                    {m.denied > 0 && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">{m.denied} denied</span>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-4 mt-2 pt-3 border-t border-slate-100">
                <div className="w-14" />
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-teal-500 inline-block"></span> Billed</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-400 inline-block"></span> Collected</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* Top Denial Reasons */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" /> Top Denial Reasons
            </h2>
            {denialReasons.length === 0 ? (
              <p className="text-slate-400 text-sm text-center py-8">No denials on record.</p>
            ) : (
              <div className="space-y-3">
                {denialReasons.map(([reason, count], idx) => (
                  <div key={idx}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-700 flex-1 mr-2">{reason}</span>
                      <span className="text-sm font-bold text-red-600">{count}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div
                        className="h-2 bg-red-400 rounded-full"
                        style={{ width: `${pct(count, maxDenialCount)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Payer Performance */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Award className="w-5 h-5 text-teal-600" /> Payer Performance
            </h2>
            {payerData.length === 0 ? (
              <p className="text-slate-400 text-sm text-center py-8">No payer data available yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold text-slate-500 border-b border-slate-200">
                      <th className="pb-2">Payer</th>
                      <th className="pb-2 text-right">Billed</th>
                      <th className="pb-2 text-right">Collected</th>
                      <th className="pb-2 text-right">Denial %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payerData.map((p) => (
                      <tr key={p.name} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2 font-medium text-slate-900 max-w-[140px] truncate">{p.name}</td>
                        <td className="py-2 text-right text-slate-700">{formatCurrency(p.billed)}</td>
                        <td className="py-2 text-right">
                          <span className={`font-semibold ${p.collectionRate >= 80 ? 'text-green-600' : p.collectionRate >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {p.collectionRate}%
                          </span>
                        </td>
                        <td className="py-2 text-right">
                          <span className={`font-semibold ${p.denialRate <= 10 ? 'text-green-600' : p.denialRate <= 25 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {p.denialRate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Claims Status Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-600" /> Claims Status Breakdown
          </h2>
          {(() => {
            const colors = {
              'Paid': 'bg-green-500', 'Pending': 'bg-yellow-400', 'Submitted': 'bg-blue-400',
              'In Review': 'bg-blue-500', 'Denied': 'bg-red-500', 'Appealed': 'bg-orange-400'
            };
            const total = statusCounts.reduce((s, c) => s + c.count, 0);
            if (total === 0) return <p className="text-slate-400 text-sm text-center py-4">No claims data yet.</p>;
            return (
              <div className="space-y-3">
                {statusCounts.map(({ status, count }) => (
                  <div key={status} className="flex items-center gap-4">
                    <div className="w-24 text-sm font-medium text-slate-700">{status}</div>
                    <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                      <div className={`h-full ${colors[status] || 'bg-slate-400'} rounded-full`} style={{ width: `${pct(count, total)}%` }} />
                    </div>
                    <div className="w-20 text-right text-sm">
                      <span className="font-semibold text-slate-900">{count}</span>
                      <span className="text-slate-400 ml-1">({pct(count, total)}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

      </div>
    </div>
  );
};

export default Performance;
